/**
 * Erreurs d'API vues par le back-office (DECISIONS §4.1, §4.3).
 *
 * Différence assumée avec `apps/web` : le back-office est un outil interne, pas une interface de
 * joueur. Il **affiche le message français renvoyé par le serveur** (`messageServeur`) et les
 * détails de validation, parce que c'est précisément ce qui permet à un administrateur de
 * corriger un brouillon invalide. Le catalogue local fournit le libellé de repli quand le
 * serveur n'a rien dit (réseau coupé, réponse hors contrat).
 */
import { ERROR_CODES } from '@elemora/shared/api';

import type { ErrorCode } from '@elemora/shared/api';

/** Vrai si la valeur est un code d'erreur du contrat (liste fermée §4.3). */
export function estCodeErreur(valeur: unknown): valeur is ErrorCode {
  return typeof valeur === 'string' && (ERROR_CODES as readonly string[]).includes(valeur);
}

/** Paramètres de construction d'une `ErreurApi`. */
export interface ParametresErreurApi {
  readonly code: ErrorCode;
  /** Message technique (journaux, tests) : peut être vide côté serveur. */
  readonly message: string;
  readonly statutHttp: number;
  /** Message français du serveur, affichable tel quel dans le back-office. */
  readonly messageServeur?: string;
  readonly details?: Record<string, unknown>;
  readonly requestId?: string;
  /** `true` si l'appel n'a jamais atteint le serveur (réseau coupé, API arrêtée). */
  readonly reseau?: boolean;
}

/** Erreur normalisée : tout échec d'appel devient une `ErreurApi`, jamais une exception brute. */
export class ErreurApi extends Error {
  readonly code: ErrorCode;
  readonly statutHttp: number;
  readonly messageServeur: string | undefined;
  readonly details: Record<string, unknown> | undefined;
  readonly requestId: string | undefined;
  readonly reseau: boolean;

  constructor(parametres: ParametresErreurApi) {
    super(parametres.message);
    this.name = 'ErreurApi';
    this.code = parametres.code;
    this.statutHttp = parametres.statutHttp;
    this.messageServeur = parametres.messageServeur;
    this.details = parametres.details;
    this.requestId = parametres.requestId;
    this.reseau = parametres.reseau === true;
  }
}

/** Normalise n'importe quelle exception en `ErreurApi` (jamais de `unknown` qui fuit). */
export function versErreurApi(inconnu: unknown): ErreurApi {
  if (inconnu instanceof ErreurApi) return inconnu;
  return new ErreurApi({
    code: 'INTERNAL_ERROR',
    message: inconnu instanceof Error ? inconnu.message : 'Erreur inconnue',
    statutHttp: 0,
    reseau: true,
  });
}

/**
 * Erreurs de validation détaillées par le serveur (`details.erreurs[]`).
 *
 * Le filtre de validation de l'API renvoie `{ chemin, message }` par problème : c'est la matière
 * première de l'affichage « ce qui bloque la publication ». Une forme inattendue est ignorée
 * plutôt que de faire planter l'écran.
 */
export function erreursDeValidation(
  erreur: ErreurApi,
): readonly { chemin: string; message: string }[] {
  const brut = erreur.details?.['erreurs'];
  if (!Array.isArray(brut)) return [];
  const resultat: { chemin: string; message: string }[] = [];
  for (const entree of brut) {
    if (typeof entree !== 'object' || entree === null) continue;
    const chemin = (entree as Record<string, unknown>)['chemin'];
    const message = (entree as Record<string, unknown>)['message'];
    if (typeof chemin !== 'string' || typeof message !== 'string') continue;
    resultat.push({ chemin, message });
  }
  return resultat;
}
